import { AxiosInstance } from "axios"
import { CreateProductGroup, ProductGroup, ProductGroupCode, ProductGroupItem } from "./productGroupTypes"

type ServiceResponse<ResponseType> = Readonly<
    ResponseType & {
        serviceResponse: {
            displayMessage: string
        }
    }
>

export enum ProductGroupQuery {
    LIST = "product-group-list-for-merchant",
    DETAILS = "get-product-group-details",
}

type FetchProductGroupsParam = Readonly<{
    merchantId: number
}>

export type FetchProductGroupsResponse = {
    productGroups: ProductGroupItem[]
}

export async function fetchProductGroups(
    params: FetchProductGroupsParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchProductGroupsResponse> {
    const { merchantId } = params
    const url = `/merchants/${merchantId}/productGroups`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// Get product Group Details
type GetProductGroupDetailsParams = Readonly<{
    merchantId: number
    productGroupId: number
}>
export type GetProductGroupDetailsResponse = Readonly<{
    productGroup: ProductGroup
}>

export async function getProductGroupDetails(
    params: GetProductGroupDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetProductGroupDetailsResponse> {
    const { merchantId, productGroupId } = params
    const url = `/merchants/${merchantId}/productGroups/${productGroupId}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// Create Product Group

type CreateProductGroupParams = Readonly<{
    merchantId: number
    productGroup: CreateProductGroup
}>
type CreateProductGroupResponse = ServiceResponse<{}>

export async function createProductGroup(
    params: CreateProductGroupParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateProductGroupResponse> {
    const { merchantId, productGroup } = params
    const url = `/merchants/${merchantId}/productGroups`

    const response = await axios.post(
        url,
        {
            productGroup,
        },
        {
            signal,
        },
    )

    return response.data
}

// Update Product Group

type UpdateProductGroupParams = Readonly<{
    merchantId: number
    productGroupId: number
    productGroup: CreateProductGroup
}>
type UpdateProductGroupResponse = ServiceResponse<{}>

export async function updateProductGroup(
    params: UpdateProductGroupParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UpdateProductGroupResponse> {
    const { merchantId, productGroup, productGroupId } = params
    const url = `/merchants/${merchantId}/productGroups/${productGroupId}`

    const response = await axios.post(
        url,
        {
            productGroup,
        },
        {
            signal,
        },
    )

    return response.data
}

// Delete Product Group

type DeleteProductGroupParam = Readonly<{
    productGroupId: number
    merchantId: number
}>

type DeleteProductGroupResponse = ServiceResponse<{}>

export async function deleteProductGroup(
    params: DeleteProductGroupParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeleteProductGroupResponse> {
    const { productGroupId, merchantId } = params
    const url = `merchants/${merchantId}/productGroups/${productGroupId}`

    const response = await axios.delete(url, {
        signal,
    })

    return response.data
}

// Fetch Product group codes
type FetchProductGroupCodesParam = Readonly<{
    merchantId: number
}>

export type FetchProductGroupCodesResponse = Readonly<{
    productGroupCodes: ProductGroupCode[]
}>

export async function fetchProductGroupCodes(
    params: FetchProductGroupCodesParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchProductGroupCodesResponse> {
    const { merchantId } = params
    const url = `merchants/${merchantId}/productGroups/codes`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// -- Move product group --
export type MoveProductGroupBody = Readonly<{
    shop: {
        id: string
    }
    moveTo: {
        position: number
    }
}>

type MoveProductGroupParams = Readonly<{
    productGroupId: number
    position: MoveProductGroupBody
}>

type MoveProductGroupResponse = ServiceResponse<{}>

export async function moveProductGroup(
    params: MoveProductGroupParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MoveProductGroupResponse> {
    const { productGroupId, position } = params
    const response = await axios.post(`/productGroups/${productGroupId}/move`, position, {
        signal,
    })

    return response.data
}
